import api, { ApiResponse } from './api';
// Studio work always belongs to a project, which carries its workspace.
const target = (projectId?: string): Record<string, string> => {
  if (!projectId) throw new Error('Open a project first');
  return { projectId };
};
const appendTarget = (form: FormData, projectId?: string) => {
  for (const [k, v] of Object.entries(target(projectId))) form.append(k, v);
};

export interface CameraOptions {
  angle?: string;
  focalLength?: string;
  depthOfField?: string;
}

export interface GenerateOptions {
  aspectRatio?: string;
  style?: string;
  camera?: CameraOptions;
}

export interface GenerationResponse {
  image: string;
  mimeType: string;
  description?: string;
  assetId?: string;
  gcsPath?: string;
}

export type ImageMode = 'startFrame' | 'endFrame' | 'startEnd' | 'style' | 'asset';

export interface VariantsResponse {
  variants: GenerationResponse[];
}

export interface StudioGeneration {
  id: string;
  type: 'generate' | 'edit' | 'blend' | 'variants';
  prompt: string;
  result_gcs_path: string | null;
  thumbnail_base64: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface VideoGenerateOptions {
  aspectRatio: '16:9' | '9:16';
  duration: '4s' | '6s' | '8s';
  audioGeneration: boolean;
  numberOfVideos: 1 | 2;
  enhancePrompt?: boolean;
  quality?: 'lite' | 'fast' | 'standard';
}

export interface VideoGenerateResponse {
  operationId: string;
  jobId: string;
}

export interface TranscriptionEntry {
  text: string;
  startMs: number;
  endMs: number;
}

export interface VideoStatusResponse {
  status: 'pending' | 'processing' | 'retrying' | 'completed' | 'failed' | 'timed_out';
  videoUrl?: string;
  gcsPath?: string;
  errorMessage?: string;
  assetId?: string;
  retryCount?: number;
  maxRetries?: number;
  correlationId?: string;
  timeoutAt?: string;
}

export interface TextOverlayStyle {
  x?: number;
  y?: number;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: number;
  color?: string;
  backgroundColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  shadow?: boolean;
  align?: 'left' | 'center' | 'right';
}

export const studioService = {
  generate: async (prompt: string, options?: GenerateOptions, projectId?: string): Promise<GenerationResponse> => {
    const response = await api.post<ApiResponse<GenerationResponse>>('/studio/generate', {
      prompt,
      options,
      ...target(projectId),
    });
    return response.data.data!;
  },

  edit: async (image: File, instruction: string, projectId?: string): Promise<GenerationResponse> => {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('instruction', instruction);
    appendTarget(formData, projectId);

    const response = await api.post<ApiResponse<GenerationResponse>>('/studio/edit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  blend: async (images: File[], instruction: string, projectId?: string): Promise<GenerationResponse> => {
    const formData = new FormData();
    images.forEach((image) => formData.append('images', image));
    formData.append('instruction', instruction);
    appendTarget(formData, projectId);

    const response = await api.post<ApiResponse<GenerationResponse>>('/studio/blend', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  variants: async (prompt: string, count: number, options?: GenerateOptions): Promise<VariantsResponse> => {
    const response = await api.post<ApiResponse<VariantsResponse>>('/studio/variants', {
      prompt,
      count,
      options,
      ...target(),
    });
    return response.data.data!;
  },

  upscale: async (image: File): Promise<GenerationResponse> => {
    const formData = new FormData();
    formData.append('image', image);
    appendTarget(formData);

    const response = await api.post<ApiResponse<GenerationResponse>>('/studio/upscale', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  uploadImage: async (file: File, projectId?: string): Promise<GenerationResponse> => {
    const formData = new FormData();
    formData.append('image', file);
    appendTarget(formData, projectId);
    formData.append('name', file.name);
    const response = await api.post<ApiResponse<GenerationResponse>>('/studio/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  save: async (imageData: string, name: string, tags?: string[]): Promise<StudioGeneration> => {
    const response = await api.post<ApiResponse<StudioGeneration>>('/studio/save', {
      imageData,
      name,
      tags,
      ...target(),
    });
    return response.data.data!;
  },

  getHistory: async (offset = 0, limit = 50): Promise<{ data: StudioGeneration[]; total: number }> => {
    const response = await api.get<ApiResponse<StudioGeneration[]>>('/studio/history', {
      params: { offset, limit },
    });
    return {
      data: response.data.data!,
      total: response.data._meta?.total ?? 0,
    };
  },

  // Video methods
  generateVideo: async (prompt: string, options: VideoGenerateOptions, projectId?: string, provider: 'veo' | 'sora' = 'veo'): Promise<VideoGenerateResponse> => {
    const response = await api.post<ApiResponse<VideoGenerateResponse>>('/studio/video/generate', {
      prompt,
      options,
      provider,
      ...target(projectId),
    });
    return response.data.data!;
  },

  videoFromImages: async (
    images: File[],
    prompt: string,
    options: VideoGenerateOptions,
    imageMode: ImageMode = 'startFrame',
    projectId?: string,
    provider: 'veo' | 'sora' = 'veo',
  ): Promise<VideoGenerateResponse> => {
    const formData = new FormData();
    images.forEach((image) => formData.append('images', image));
    formData.append('prompt', prompt);
    formData.append('options', JSON.stringify(options));
    formData.append('imageMode', imageMode);
    formData.append('provider', provider);
    appendTarget(formData, projectId);

    const response = await api.post<ApiResponse<VideoGenerateResponse>>('/studio/video/from-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  extendVideo: async (
    video: File | null,
    prompt: string,
    options: VideoGenerateOptions,
    projectId?: string,
    provider: 'veo' | 'sora' = 'veo',
    sourceJobId?: string,
  ): Promise<VideoGenerateResponse> => {
    const formData = new FormData();
    if (video) formData.append('video', video);
    formData.append('prompt', prompt);
    formData.append('options', JSON.stringify(options));
    formData.append('provider', provider);
    if (sourceJobId) formData.append('sourceJobId', sourceJobId);
    // A Sora extension without a project stays in its source video's workspace.
    if (projectId || !sourceJobId) appendTarget(formData, projectId);

    const response = await api.post<ApiResponse<VideoGenerateResponse>>('/studio/video/extend', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  checkVideoStatus: async (jobId: string): Promise<VideoStatusResponse> => {
    const response = await api.get<ApiResponse<VideoStatusResponse>>(`/studio/video/status/${jobId}`);
    return response.data.data!;
  },

  saveVideo: async (jobId: string, name: string): Promise<{ saved: boolean }> => {
    const response = await api.post<ApiResponse<{ saved: boolean }>>('/studio/video/save', {
      jobId,
      name,
    });
    return response.data.data!;
  },

  transcribeVideo: async (videoUrl: string): Promise<{ subtitles: TranscriptionEntry[]; hasAudio: boolean }> => {
    const response = await api.post<ApiResponse<{ subtitles: TranscriptionEntry[]; hasAudio: boolean }>>('/studio/video/transcribe', {
      videoUrl,
    });
    return response.data.data!;
  },

  burnSubtitles: async (videoUrl: string, subtitles: TranscriptionEntry[], projectId?: string): Promise<{ videoUrl: string; gcsPath: string; durationMs: number }> => {
    const response = await api.post<ApiResponse<{ videoUrl: string; gcsPath: string; durationMs: number }>>('/studio/video/burn-subtitles', {
      videoUrl,
      subtitles,
      ...target(projectId),
    });
    return response.data.data!;
  },

  textOverlay: async (
    imageUrl: string,
    text: string,
    style?: TextOverlayStyle,
    projectId?: string,
    preset = 'headline'
  ): Promise<GenerationResponse> => {
    const response = await api.post<ApiResponse<GenerationResponse>>('/studio/text-overlay', {
      imageUrl,
      text,
      style,
      ...target(projectId),
      preset,
    });
    return response.data.data!;
  },

  concatVideos: async (jobIds: string[], stripAudio?: boolean): Promise<{ gcsPath: string; signedUrl: string; durationMs: number }> => {
    const response = await api.post<ApiResponse<{ gcsPath: string; signedUrl: string; durationMs: number }>>('/studio/video/concat', {
      jobIds,
      stripAudio,
    });
    return response.data.data!;
  },

  extractFrame: async (jobId: string, timestamp: 'last' | 'first' | string): Promise<{ gcsPath: string; signedUrl: string }> => {
    const response = await api.post<ApiResponse<{ gcsPath: string; signedUrl: string }>>('/studio/video/extract-frame', {
      jobId,
      timestamp,
    });
    return response.data.data!;
  },

  compositeImages: async (
    baseGcsPath: string,
    overlayGcsPath: string,
    options?: { x?: string; y?: string; scale?: number; opacity?: number },
    projectId?: string,
  ): Promise<{ gcsPath: string; signedUrl: string }> => {
    const response = await api.post<ApiResponse<{ gcsPath: string; signedUrl: string }>>('/studio/video/composite-images', {
      baseGcsPath,
      overlayGcsPath,
      ...options,
      ...target(projectId),
    });
    return response.data.data!;
  },
};
